import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { GAME_STAGES, DEFAULT_ROOM } from "../constants/gameStages";
import socket from "../socket/socket";
import { useGameStage } from "../hooks/useGameStage";
import { useRoleBackground } from "../hooks/useRoleBackground";
import { useLanguage } from "../context/LanguageContext";

import originalGraphic from "../assets/original_graphic_r.png";
// Challenge mode graphic no longer used
// import otherGraphic from "../assets/in_other_words_graphic_r.png";

function GamemodeV2({ role, gameStage }) {
  const { updateStage } = useGameStage();
  const { t } = useLanguage();
  const [confirmedGamemode, setConfirmedGamemode] = useState(null); // synced via socket

  const [warning, setWarning] = useState("");
  const navigate = useNavigate();
  // Use the actual role for background color (describer = pink, guesser = blue)
  const { bgStyle, className } = useRoleBackground(role === "describer" ? "pink" : "blue");
  const destination = "/showgamemode";

  // Debug: Log role changes
  useEffect(() => {
    console.log("🎮 GamemodeV2 rendered with role:", role, "| gameStage:", gameStage);
  }, [role, gameStage]);

  // On mount, guesser requests the current mode
  useEffect(() => {
    if (role === "guesser") {
      socket.emit("get_gamemode", { room: DEFAULT_ROOM });
    }
  }, [role]);

  // Describer responds if mode already selected
  useEffect(() => {
    const handleGetGamemode = () => {
      if (role === "describer" && confirmedGamemode) {
        socket.emit("gamemode_selected", {
          mode: confirmedGamemode,
          room: DEFAULT_ROOM,
        });
      }
    };
    socket.on("get_gamemode", handleGetGamemode);
    return () => socket.off("get_gamemode", handleGetGamemode);
  }, [role, confirmedGamemode]);

  // Receive selected mode from describer
  useEffect(() => {
    const handleGamemodeSelected = (data) => {
      if (data?.mode) {
        setConfirmedGamemode(data.mode);
        console.log("✅ Confirmed gamemode received:", data.mode);
      }
    };
    socket.on("gamemode_selected", handleGamemodeSelected);
    return () => socket.off("gamemode_selected", handleGamemodeSelected);
  }, []);

  // Reset mode only when returning to WAITING (new game), not READY (new round)
  useEffect(() => {
    if (gameStage === GAME_STAGES.WAITING) {
      console.log("🔄 Resetting gamemode selection state for new game | gameStage:", gameStage);
      setConfirmedGamemode(null);
    }
  }, [gameStage]);

  // DO NOT reset gamemode when role changes during READY state
  // The gamemode should persist across rounds in the same session

  const handleModeClick = (mode) => {
    console.log("🎮 Mode clicked:", mode, "| role:", role, "| gameStage:", gameStage, "| confirmedGamemode:", confirmedGamemode);

    if (role === "describer") {
      // confirm, broadcast, and navigate
      setConfirmedGamemode(mode);
      console.log("📤 Describer emitting gamemode_selected and gamemode_confirmed");
      socket.emit("gamemode_selected", { mode, room: DEFAULT_ROOM });
      socket.emit("gamemode_confirmed", { mode, room: DEFAULT_ROOM });
      console.log("🚀 Navigating to:", destination);
      navigate(destination, { state: { mode: mode } });
      if (gameStage === GAME_STAGES.READY)
        updateStage(GAME_STAGES.WAITING_FOR_DESCRIBER);
      return;
    } else if (role === "guesser") {
      if (confirmedGamemode !== mode) {
        console.log("⚠️ Guesser clicked but mode not confirmed by describer yet");
        setWarning(t('warning.waitForDescriber', 'Wait for the describer to confirm the mode.'));
        setTimeout(() => setWarning(""), 3000);
        return;
      }
      console.log("✅ Guesser proceeding with confirmed mode");
      navigate(destination, { state: { mode: mode } });
      if (gameStage === GAME_STAGES.READY)
        updateStage(GAME_STAGES.WAITING_FOR_DESCRIBER);
    }
  };

  // Removed unused functions since only one mode is displayed
  // const isActive = (mode) => {
  //   return role === "describer"
  //     ? hoveredGamemode === mode
  //     : confirmedGamemode === mode;
  // };

  return (
    <div
      className={`flex flex-col justify-center items-center ${className}`}
      style={bgStyle}
    >
      <div className="mt-[4px] my-[60px] w-[1052px] bg-white rounded-lg shadow-lg flex flex-col items-center text-center">
        <h2 className={`${role === "describer" ? "bg-pink400" : "bg-blue400"} w-full rounded-t-lg font-bold text-white text-xl font-[22px] py-[16px] px-[12px] mb-[24px]`}>
          {t('gamemode.select', 'SELECT A GAMEMODE:')}
        </h2>
        <div className="px-[60px] flex justify-center mb-[32px] rounded-lg h-[520px] w-[1052px]">
          {/* Normal Mode - Centered */}
          <div className="flex flex-col justify-between items-center w-[452px] h-full rounded-xl border-[3px] border-[#FFD2F3]">
            <div className="flex flex-col items-center flex-grow">
              <div className="mt-[21px] my-[23px] w-full">
                <h2 className="text-black font-bold text-[26px]">
                  {t('gamemode.mindOverMachine.title', 'MIND OVER MACHINE')}
                </h2>
              </div>
              <div>
                <img
                  src={originalGraphic}
                  alt="graphic icon"
                  className="mt-[20px] w-[216px] h-[123px] object-cover rounded"
                />
              </div>
              <div className="mt-[4px] mx-[19px] flex-grow flex items-center">
                <p className="text-black text-[20px] font-normal">
                  {t('gamemode.mindOverMachine.description', 'Can you outwit AI? Give clever clues that guide your teammate but confuse the computer. Find the gaps in machine understanding and win with human smarts!')}
                </p>
              </div>
            </div>
            <div>
              <button
                className="btn-pink-sm rounded-lg mt-[23px] mb-[34px]"
                onClick={() => handleModeClick("normal")}
              >
                {t('button.select', 'Select!')}
              </button>
            </div>
          </div>

          {/* In Other Words Mode - Hidden */}
          {/* Challenge mode is currently disabled and hidden from the UI */}

          {warning && <div className="text-red-500 mb-2">{warning}</div>}
        </div>
      </div>
    </div>
  );
}

export default GamemodeV2;
